/* eslint-disable react/no-danger */

import React, { useState, useRef, useEffect } from 'react';

import PlayButton from '../../../../../components/chat-tools-fool-componets/PlayButton';
import PauseButton from '../../../../../components/chat-tools-fool-componets/PauseButton';
import FullScreenButton from '../../../../../components/chat-tools-fool-componets/FullScreenButton';
import SoundButton from '../../../../../components/chat-tools-fool-componets/SoundButton';

import {
  convertSecondsToTime, defineClientX, clamp,
} from './utils';
import getActualEventName from '../../../../../libs/utils/getActualEventName';

import './MediaPlayer.scss';

interface MediaPlayerProps {
  isAudio?: boolean;
  isVideo?: boolean;
  src: string;
  text?: string;
}

interface VolumeState {
  isShowControl: boolean;
  level: number;
  isChanging: boolean;
}

const VOLUME_STEP = 0.05;
const SEEK_STEP = 5;

const KEY_ENTER = 'Enter';
const KEY_SPACE = ' ';
const KEY_ARROW_LEFT = 'ArrowLeft';
const KEY_ARROW_RIGHT = 'ArrowRight';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ARROW_DOWN = 'ArrowDown';

const MediaPlayer: React.FC<MediaPlayerProps> = ({ isAudio, isVideo, src }) => {
  const [currentTime, setCurrentTime] = useState<string>('0:00');
  const [totalTime, setTotalTime] = useState<string>('');
  const [playingProgress, setPlayingProgress] = useState<number>(0);
  const [heightToWidthRatioOfVideo, setHeightToWidthRatioOfVideo] = useState<string>('56.25%');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const volumeStateRef = useRef<VolumeState>({
    isShowControl: false,
    level: 100,
    isChanging: false,
  });
  const [volume, setVolume] = useState<VolumeState>(volumeStateRef.current);

  useEffect(() => {
    volumeStateRef.current = volume;
  }, [volume]);

  const progressSlider = useRef<HTMLDivElement>(null);
  const volumeControls = useRef<HTMLDivElement>(null);
  const volumeSlider = useRef<HTMLDivElement>(null);
  const player = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const progress = useRef<HTMLDivElement>(null);

  const onTimeUpdate = (): void => {
    if (!player.current || !player.current.duration) return;

    let current = player.current.currentTime;
    let percent = current / player.current.duration * 100;

    setCurrentTime(convertSecondsToTime(current));
    setPlayingProgress(Number(percent.toFixed(2)));
  };

  const onTogglePlay = (): void => {
    if (isPlaying) {
      player.current?.pause();
    } else {
      player.current?.play();
    }

    setIsPlaying(!isPlaying);
  };

  const handleUpdateProgress = (event: Event): void => {
    if (!player.current || !player.current.duration || !progressSlider.current) return;

    const progress = defineClientX(event as MouseEvent | TouchEvent, progressSlider.current);

    setPlayingProgress(progress * 100);
    setCurrentTime(convertSecondsToTime(player.current.duration * progress));
    player.current.currentTime = Number((player.current.duration * progress).toFixed(2));
  };

  const handleStartChangeProgress = (event: Event): void => {
    handleUpdateProgress(event);
    window.addEventListener(getActualEventName('mousemove'), handleUpdateProgress);
    window.addEventListener(getActualEventName('mouseup'), handleFinishChangeProgress);
  };

  const handleFinishChangeProgress = (): void => {
    window.removeEventListener(getActualEventName('mousemove'), handleUpdateProgress);
    window.removeEventListener(getActualEventName('mouseup'), handleFinishChangeProgress);
  };

  const onToggleVolumeControl = (): void => {
    setVolume(prev => ({ ...prev, isShowControl: !prev.isShowControl }));
  };

  const handleUpdateVolume = (event: Event): void => {
    if (!volumeSlider.current || !player.current) return;

    const sliderRect = volumeSlider.current.getBoundingClientRect();
    const sliderHeight = sliderRect.height;

    const clickY = (event as MouseEvent).clientY - sliderRect.top;
    const relativePosition = 1 - (clickY / sliderHeight);
    const boundedPosition = clamp(relativePosition, 0, 1);

    setVolume(prev => ({ ...prev, level: boundedPosition * 100, isChanging: true }));
    player.current.volume = boundedPosition;
  };

  const handleStartChangeVolume = (event: Event): void => {
    handleUpdateVolume(event);

    window.addEventListener(getActualEventName('mousemove'), handleUpdateVolume);
    window.addEventListener(getActualEventName('mouseup'), handleFinishChangeVolume);
  };

  const handleFinishChangeVolume = () => {
    window.removeEventListener(getActualEventName('mousemove'), handleUpdateVolume);
    window.removeEventListener(getActualEventName('mouseup'), handleFinishChangeVolume);
    setVolume(prev => ({ ...prev, isChanging: false }));
  };

  const handleClickOutside = (event: Event): void => {
    if (volumeStateRef.current.isShowControl &&
        volumeControls.current &&
        !volumeControls.current.contains(event.target as Node)) {
      setVolume(prev => ({ ...prev, isShowControl: false }));
    }
  };

  const createKeyDownHandler = (callback: () => void) => {
    return (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === KEY_ENTER || event.key === KEY_SPACE) {
        event.preventDefault();
        callback();
      }
    }
  }
  
  const updateVolume = (newVolume: number): void => {
    if (!player.current) return;
    const boundedVolume = clamp(newVolume, 0, 1);
    player.current.volume = boundedVolume;
    setVolume(prev => ({ ...prev, level: boundedVolume * 100 }));
  }

  const handleVolumeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (volume.isShowControl && player.current) {
      const currentVolume = player.current.volume;
      
      switch (event.key) {
        case KEY_ARROW_UP:
          event.preventDefault();
          updateVolume(currentVolume + VOLUME_STEP);
          return;
        case KEY_ARROW_DOWN:
          event.preventDefault();
          updateVolume(currentVolume - VOLUME_STEP);
          return;
      }
    }
  };

  const updateTimeAndProgress = (newTime: number, duration: number): void => {
    setCurrentTime(convertSecondsToTime(newTime));
    setPlayingProgress((newTime / duration) * 100);
  }

  const seekTime = (delta: number): void => {
    if (!player.current || !player.current.duration) return;
    
    const currentTime = player.current.currentTime;
    const duration = player.current.duration;
    const newTime = clamp(currentTime + delta, 0, duration);
    
    player.current.currentTime = newTime;
    updateTimeAndProgress(newTime, duration);
  }

  const handleTimeSkipKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!player.current || !player.current.duration) return;

    switch (event.key) {
      case KEY_ARROW_LEFT:
        event.preventDefault();
        seekTime(-SEEK_STEP);
        return;
      case KEY_ARROW_RIGHT:
        event.preventDefault();
        seekTime(SEEK_STEP);
        return;
    }
  }
  
  useEffect(() => {
    if (!progressSlider.current || !volumeSlider.current || !player.current) return;

    progressSlider.current.addEventListener(getActualEventName('mousedown'), handleStartChangeProgress);
    volumeSlider.current.addEventListener(getActualEventName('mousedown'), handleStartChangeVolume);
    player.current.addEventListener('timeupdate', onTimeUpdate);

    document.addEventListener('mousedown', handleClickOutside);

    player.current.addEventListener('loadedmetadata', () => {
      if (!player.current || !player.current.duration) return;

      if (player.current.duration === Infinity) {
        progressSlider.current?.removeEventListener(
          getActualEventName('mousedown'),
          handleStartChangeProgress
        );
      } else {
        setTotalTime(convertSecondsToTime(player.current.duration));
      }

      if ('videoWidth' in player.current && player.current.videoWidth) {
        const newHeightToWidthRatioOfVideo = `${
          player.current.videoHeight / player.current.videoWidth * 100}%`;
        setHeightToWidthRatioOfVideo(newHeightToWidthRatioOfVideo);
      }
    });

    player.current.addEventListener('ended', () => {
      setCurrentTime('0:00');
      setIsPlaying(false);
      setPlayingProgress(0);
    });

    return () => {
      if (progressSlider.current) {
        progressSlider.current.removeEventListener(getActualEventName('mousedown'), handleStartChangeProgress);
      }
      if (volumeSlider.current) {
        volumeSlider.current.removeEventListener(getActualEventName('mousedown'), handleStartChangeVolume);
      }
      if (player.current) {
        player.current.removeEventListener('timeupdate', onTimeUpdate);
        player.current.removeEventListener('loadedmetadata', () => {});
        player.current.removeEventListener('ended', () => {});
      }
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener(getActualEventName('mousemove'), handleUpdateProgress);
      window.removeEventListener(getActualEventName('mouseup'), handleFinishChangeProgress);
      window.removeEventListener(getActualEventName('mousemove'), handleUpdateVolume);
      window.removeEventListener(getActualEventName('mouseup'), handleFinishChangeVolume);
    };
  }, []);

  const isMediaPlayerSupported = player.current && player.current.duration
    && player.current.duration !== Infinity &&
    (isAudio || ('videoHeight' in player.current && player.current.videoHeight > 0));

  let playerElement: React.ReactNode = null;
  if (isVideo) {
    playerElement = (
      <div className="video-wrapper">
        <div className="before-video" style={{ paddingTop: heightToWidthRatioOfVideo }} />
        <video ref={player as React.RefObject<HTMLVideoElement>} onClick={onTogglePlay}>
          <source src={src} />
          <track kind="captions" />
        </video>
      </div>
    );
  } else if (isAudio) {
    playerElement = (
      <audio ref={player}>
        <source src={src} />
        <track kind="captions" />
      </audio>
    );
  }

  return (
    <div
      className={`media-player ${isAudio ? 'audio' : ''}`}
      dir="ltr"
      style={{ display: isMediaPlayerSupported ? '' : 'none' }}
      tabIndex={0}
      onKeyDown={handleTimeSkipKeyDown}
    >
      <div className={isVideo ? 'control-wrapper-video' : 'control-wrapper'}>
        <div 
          className="play-pause-btn"
          role="button"
          tabIndex={0}
          onClick={onTogglePlay}
          onKeyDown={createKeyDownHandler(onTogglePlay)}
        >
          {isPlaying ? <PauseButton /> : <PlayButton /> }
        </div>

        <div className="controls">
          <span className="current-time">
            {currentTime}
          </span>
          <div
            className="slider"
            data-direction="horizontal"
            role="slider"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={playingProgress}
            tabIndex={0}
            ref={progressSlider}
          >
            <div className="progress" style={{ width: `${playingProgress}%` }} ref={progress}>
              <div className="pin" id="progress-pin" data-method="rewind" />
            </div>
          </div>
          <span className="total-time">
            {totalTime}
          </span>
          <span className="time-for-small-screens">
            {isPlaying || currentTime !== '0:00' ? currentTime : totalTime}
          </span>
        </div>

        {isVideo && 
          <FullScreenButton playerRef={player}/>
        }

        <div className="volume" ref={volumeControls}>
          <div
            className={`volume-btn ${volume.isShowControl ? 'open' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onToggleVolumeControl}
            onKeyDown={(event) => {
              handleVolumeKeyDown(event);
              createKeyDownHandler(onToggleVolumeControl)(event);
            }}
          >
            <li className="sound-btn sound-on">
              <SoundButton volumeLevel={volume.level} />
            </li>
          </div>
          <div className={`volume-controls ${volume.isShowControl ? '' : 'hidden'}`}>
            <div
              className="slider"
              data-direction="vertical"
              role="slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={volume.level}
              tabIndex={0}
              ref={volumeSlider}
            >
              <div className="progress" style={{ height: `${volume.level}%` }}>
                <div className="pin" id="volume-pin" data-method="changeVolume" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {playerElement}
    </div>
  );
};

export default MediaPlayer;
